// src/components/Drive/DeliveryDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  ArrowLeft as ArrowBack,
  MapPin as LocationOn,
  User as Person,
  Phone,
  CircleCheck as CheckCircle,
  Play as PlayArrow,
  Clock as Schedule,
  Map as MapIcon,
  Bus,
  Package,
  ArrowRight,
  Banknote,
  Route,
  RotateCcw,
  Thermometer,
  DoorOpen,
  TriangleAlert as AlertTriangle,
  Building2,
  KeyRound,
  Layers,
} from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";

import { subscribeToDelivery } from "../../firebase/deliveries";
import { pingCourierLocationFn, revertDeliveryFn } from "../../firebase/deliveryFunctions";
import { getCourierLocationPayload } from "../../utils/location";

import { useDelivery } from "../../context/DeliveryContext";
import { useTheme } from "../../context/ThemeContext";
import { ReportProblemModal } from "./DeliveryList/ReportProblemModal";

const STATUS_CONFIG = {
  active: {
    label: "Saatavilla",
    color: "default",
    nextAction: "Hyväksy kuljetus",
    canReport: false,
  },
  ongoing: {
    label: "Meneillään",
    color: "info",
    nextAction: "Merkitse noudetuksi",
    canReport: true,
  },
  picked: {
    label: "Noudettu",
    color: "warning",
    nextAction: "Merkitse valmiiksi",
    canReport: true,
  },
  completed: {
    label: "Valmis",
    color: "success",
    nextAction: null,
    canReport: false,
  },
};

const STATUS_FLOW = {
  active: "ongoing",
  ongoing: "picked",
  picked: "completed",
};

// Address detail fields shown once the courier has taken on the leg
const DETAIL_FIELDS = [
  { key: "building", label: "Rakennus", Icon: Building2 },
  { key: "apartment", label: "Asunto", Icon: DoorOpen },
  { key: "floor", label: "Kerros", Icon: Layers },
  { key: "accessCode", label: "Ovikoodi", Icon: KeyRound },
];

const getGoogleMapsUrl = (address) => {
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const formatPhoneLink = (phone) => {
  if (!phone) return null;
  return `tel:${String(phone).replace(/[^\d+]/g, "")}`;
};

const formatTime = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d.toLocaleString("fi-FI", { dateStyle: "short", timeStyle: "short" });
  }
  const d = value instanceof Date ? value
    : typeof value === "number" ? new Date(value < 1e12 ? value * 1000 : value)
    : new Date(value);
  return isNaN(d.getTime()) ? null : d.toLocaleString("fi-FI", { dateStyle: "short", timeStyle: "short" });
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const formatDistance = (meters) => {
  if (!meters || meters <= 0) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

// ─── Reusable address block ───
function AddressBlock({ label, address, details, showDetails, name, phone, time, timeLabel, mapUrl, colors }) {
  const instructions = details?.instructions;

  const fields = showDetails
    ? DETAIL_FIELDS
        .map((f) => ({ ...f, value: String(details?.[f.key] ?? "").trim() }))
        .filter((f) => f.value)
    : [];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
        {mapUrl && (
          <Tooltip title="Avaa kartalla">
            <IconButton size="small" component="a" href={mapUrl} target="_blank" rel="noopener noreferrer" sx={{ color: "primary.main" }}>
              <MapIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Stack spacing={0.75}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <LocationOn size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{address}</Typography>
            {instructions && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {instructions}
              </Typography>
            )}
          </Box>
        </Box>

        {fields.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              p: 1,
              borderRadius: 1.5,
              bgcolor: "action.hover",
            }}
          >
            {fields.map(({ key, label: fieldLabel, Icon, value }) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Icon size={16} style={{ flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  {fieldLabel}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    ...(key === "accessCode" && { fontFamily: "monospace", letterSpacing: 0.5 }),
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {name && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Person size={18} style={{ flexShrink: 0 }} />
            <Typography variant="body2">{name}</Typography>
          </Box>
        )}

        {phone && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Phone size={18} style={{ flexShrink: 0 }} />
            <Typography
              variant="body2"
              component="a"
              href={formatPhoneLink(phone)}
              sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              {phone}
            </Typography>
          </Box>
        )}

        {time && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Schedule size={18} style={{ flexShrink: 0 }} />
            <Typography variant="body2">
              {timeLabel}: <strong>{time}</strong>
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Bus info block ───
function BusInfoBlock({ info, label, severity, colors }) {
  if (!info) return null;

  const busLabel = [info.exactName, info.shortName, info.busCompany].filter(Boolean).join(" • ");
  const routeLabel = [info.origin, info.destination].filter(Boolean).join(" → ");
  const timeLabel = [formatTime(info.start), formatTime(info.end)].filter(Boolean).join(" → ");

  return (
    <Alert
      severity={severity}
      icon={<Bus size={18} />}
      sx={{
        border: `1px solid ${colors.surface?.border || "rgba(0,0,0,0.12)"}`,
        bgcolor: colors.surface?.raised || "background.paper",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
      {busLabel && <Typography variant="body2" color="text.secondary">{busLabel}</Typography>}
      {routeLabel && <Typography variant="body2" sx={{ mt: 0.5 }}>{routeLabel}</Typography>}
      {timeLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          {timeLabel}
        </Typography>
      )}
      {(info.origin || info.destination) && (
        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            color="info"
            component="a"
            href="https://www.onnibus.com/bussit-kartalla"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<Bus size={14} />}
          >
            Bussin sijainti
          </Button>
        </Box>
      )}
    </Alert>
  );
}

export function DeliveryDetail({ deliveryId }) {
  const navigate = useNavigate();
  const { acceptDelivery, markAsPicked, completeDelivery } = useDelivery();
  const { colors } = useTheme();

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  // Report-problem modal (also handles "revert")
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const pingLocation = async () => {
    try {
      const loc = await getCourierLocationPayload();
      if (!loc) return;
      await pingCourierLocationFn(deliveryId, loc);
    } catch (e) {
      console.warn("pingLocation failed (ignored)", e);
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeToDelivery(
      deliveryId,
      (data) => { setDelivery(data); setLoading(false); setError(null); },
      (err) => { setError(err?.message || "Virhe haettaessa kuljetusta"); setLoading(false); }
    );
    return () => unsubscribe();
  }, [deliveryId]);

  const status = delivery?.status;
  const statusConfig = STATUS_CONFIG[status] || { label: status || "—", color: "default", nextAction: null, canReport: false };
  const titleId = useMemo(() => delivery?.deliveryId || delivery?.id || deliveryId, [delivery, deliveryId]);

  const isMultiDelivery = delivery?.deliveryType === "multi-delivery";

  // Addresses — new fields with legacy fallbacks
  const fromAddress = delivery?.originInfo?.address ?? delivery?.fromInfo?.address ?? delivery?.origin ?? delivery?.from ?? "";
  const fromDetails = delivery?.originInfo?.details ?? delivery?.fromInfo?.details ?? null;
  const toAddress = delivery?.destinationInfo?.address ?? delivery?.toInfo?.address ?? delivery?.destination ?? delivery?.to ?? "";
  const toDetails = delivery?.destinationInfo?.details ?? delivery?.toInfo?.details ?? null;

  // Access codes / apartment numbers unlock per leg, as the courier reaches it
  const showPickupDetails = status === "ongoing" || status === "picked";
  const showDeliveryDetails = status === "picked";

  // Contact
  const senderName = delivery?.senderInfo?.name;
  const senderPhone = delivery?.senderPhone;
  const receiverName = delivery?.receiverInfo?.name;
  const receiverPhone = delivery?.receiverPhone;

  // Times — new field names with legacy fallbacks
  const pickupTime = formatTime(delivery?.pickupTime ?? delivery?.pickUpTime ?? delivery?.pickupTimeMs);
  const targetTime = formatTime(delivery?.targetTime ?? delivery?.targetTimeMs);
  const completedTime = formatTime(delivery?.completedAtMs ?? delivery?.completedAt);

  // Route info
  const distance = formatDistance(delivery?.distance);
  const duration = formatDuration(delivery?.duration);

  // Reward
  const reward = delivery?.reward;

  // Flags
  const hasFlags = delivery?.returnParcels || delivery?.temperatureControl || delivery?.canBeLeftAtDoor;

  // Bus
  const outboundBusInfo = delivery?.outboundBusInfo || null;
  const inboundBusInfo = delivery?.inboundBusInfo || null;
  const hasBusHandoff = Boolean(outboundBusInfo || inboundBusInfo);

  const handleStatusUpdate = async () => {
    if (!delivery || !status) return;
    const nextStatus = STATUS_FLOW[status];
    if (!nextStatus) return;

    if (status === "active") {
      try { setUpdating(true); await acceptDelivery(deliveryId); await pingLocation(); }
      catch (err) { console.error(err); alert("Virhe hyväksyttäessä kuljetusta"); }
      finally { setUpdating(false); }
      return;
    }

    if (nextStatus === "completed") { setCompleteDialogOpen(true); return; }

    if (nextStatus === "picked") {
      try { setUpdating(true); await markAsPicked(deliveryId); await pingLocation(); }
      catch (err) { console.error(err); alert("Virhe merkittäessä noudetuksi"); }
      finally { setUpdating(false); }
    }
  };

  const handleReportSubmit = async ({ deliveryId: dId, issueType, issueLabel, details, revert }) => {
    setReportSubmitting(true);
    try {
      const functions = getFunctions();
      const reportDeliveryProblem = httpsCallable(functions, "reportDeliveryProblem");

      // 1. Send the report (always)
      await reportDeliveryProblem({
        deliveryId: dId,
        issueType,
        issueLabel,
        details,
        reverted: !!revert,
      });

      // 2. If revert was requested, do it after the report is in
      if (revert) {
        await revertDeliveryFn(dId);
        await pingLocation();
      }

      setReportOpen(false);
    } catch (err) {
      console.error("Failed to report problem", err);
      alert(err.message || "Ilmoituksen lähetys epäonnistui. Yritä uudelleen tai soita +358 50 505 8167.");
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleComplete = async () => {
    try {
      setUpdating(true);
      await completeDelivery(deliveryId);
      await pingLocation();
      setCompleteDialogOpen(false);
      setTimeout(() => navigate("/kuljeta-paketti?tab=completed"), 400);
    } catch (err) { console.error(err); alert("Virhe merkittäessä valmiiksi"); }
    finally { setUpdating(false); }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  if (error || !delivery) {
    return <Alert severity="error">{error || "Kuljetusta ei löytynyt"}</Alert>;
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack size={18} />} onClick={() => navigate("/kuljeta-paketti")} sx={{ px: 0 }}>
          Takaisin
        </Button>
      </Box>

      <Card sx={{ borderRadius: 2, border: `1px solid ${colors.surface?.border || "rgba(0,0,0,0.12)"}`, bgcolor: colors.surface?.raised || "background.paper" }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

          {/* ── Header ── */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700 }} noWrap>
                #{titleId}
              </Typography>

              {isMultiDelivery ?
                <Chip
                  icon={<Package size={14} />}
                  label="Moniosainen kuljetus"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mt: 0.75 }}
                />
                :
                  <Typography variant="body2" color="text.secondary">
                    {delivery.deliveryType}{delivery.deliveryTransportType ? ` • ${delivery.deliveryTransportType}` : ""}
                  </Typography>
              }
            </Box>
            <Chip label={statusConfig.label} color={statusConfig.color} />
          </Box>

          {/* ── Route summary bar ── */}
          {(distance || duration || reward) && (
            <Box sx={{
              display: "flex", gap: { xs: 1.5, sm: 3 }, flexWrap: "wrap", mb: 2, p: 1.5,
              borderRadius: 1.5, bgcolor: "action.hover",
            }}>
              {distance && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Route size={16} />
                  <Typography variant="body2" fontWeight={600}>{distance}</Typography>
                </Box>
              )}
              {duration && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Schedule size={16} />
                  <Typography variant="body2" fontWeight={600}>{duration}</Typography>
                </Box>
              )}
              {reward != null && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                  <Banknote size={16} />
                  <Typography variant="body2" fontWeight={700} color="primary">{reward} €</Typography>
                </Box>
              )}
            </Box>
          )}

          <Stack spacing={2.25}>

            {/* ── Pickup ── */}
            <AddressBlock
              label="Nouto"
              address={fromAddress}
              details={fromDetails}
              showDetails={showPickupDetails}
              name={senderName}
              phone={senderPhone}
              time={pickupTime}
              timeLabel={isMultiDelivery ? "Alkaa" : "Noutoaika"}
              mapUrl={getGoogleMapsUrl(fromAddress)}
              colors={colors}
            />

            <Divider sx={{ borderColor: colors.surface?.border }} />

            {/* ── Delivery ── */}
            <AddressBlock
              label="Toimitus"
              address={toAddress}
              details={toDetails}
              showDetails={showDeliveryDetails}
              name={receiverName}
              phone={receiverPhone}
              time={targetTime}
              timeLabel={isMultiDelivery ? "Loppuu" : "Tavoiteaika"}
              mapUrl={getGoogleMapsUrl(toAddress)}
              colors={colors}
            />

            {/* ── Bus handoff ── */}
            {hasBusHandoff && (
              <>
                <Divider sx={{ borderColor: colors.surface?.border }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Bussitiedot</Typography>
                  <Stack spacing={1.25}>
                    <BusInfoBlock info={outboundBusInfo} label="Seuraava vaihe: bussikuljetus" severity="info" colors={colors} />
                    <BusInfoBlock info={inboundBusInfo} label="Edellinen vaihe: bussikuljetus" severity="warning" colors={colors} />
                  </Stack>
                </Box>
              </>
            )}

            {/* ── Special flags ── */}
            {hasFlags && (
              <>
                <Divider sx={{ borderColor: colors.surface?.border }} />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {delivery.returnParcels && (
                    <Chip icon={<RotateCcw size={14} />} label="Palautus" size="small" variant="outlined" />
                  )}
                  {delivery.temperatureControl && (
                    <Chip icon={<Thermometer size={14} />} label="Lämpötilahallinta" size="small" variant="outlined" />
                  )}
                  {delivery.canBeLeftAtDoor && (
                    <Chip icon={<DoorOpen size={14} />} label="Voi jättää ovelle" size="small" variant="outlined" />
                  )}
                </Box>
              </>
            )}

            {/* ── Accepted nudge ── */}
            {status === "ongoing" && (
              <Alert
                severity="info"
                sx={{ py: 0.75 }}
                action={
                  <Button
                    size="small"
                    color="info"
                    onClick={() => navigate("/kuljeta-paketti?tab=own&view=actions")}
                  >
                    Avaa
                  </Button>
                }
              >
                Kuljetus hyväksytty — katso tehtävälista
              </Alert>
            )}

            {/* ── Action buttons ── */}
            <Box sx={{ display: "flex", gap: 1.5, flexDirection: isMobile ? "column" : "row" }}>
              {statusConfig.canReport && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="large"
                  fullWidth={isMobile}
                  onClick={() => setReportOpen(true)}
                  disabled={updating}
                  startIcon={<AlertTriangle size={18} />}
                  sx={{ py: 1.2 }}
                >
                  Ilmoita ongelma
                </Button>
              )}

              {statusConfig.nextAction && (
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleStatusUpdate}
                  disabled={updating}
                  startIcon={updating ? <CircularProgress size={20} /> : <PlayArrow size={18} />}
                  sx={{ py: 1.2 }}
                >
                  {statusConfig.nextAction}
                </Button>
              )}
            </Box>

            {/* ── Quick nav — only after accepting ── */}
            {status !== "active" && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => navigate("/kuljeta-paketti?tab=own&view=actions")}
                endIcon={<ArrowRight size={18} />}
                sx={{ py: 1.2 }}
              >
                Tehtävänäkymä
              </Button>
            )}

            {/* ── Completed badge ── */}
            {status === "completed" && (
              <Box sx={{
                p: 2, borderRadius: 2, display: "flex", gap: 1, alignItems: "center",
                border: `1px solid ${colors.semantic?.success?.border || "rgba(0,0,0,0.12)"}`,
                bgcolor: colors.semantic?.success?.background,
              }}>
                <CheckCircle size={18} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Valmis</Typography>
                  {completedTime && (
                    <Typography variant="caption" color="text.secondary">{completedTime}</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* ── Completion Dialog ── */}
      <Dialog open={completeDialogOpen} onClose={() => !updating && setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merkitse valmiiksi</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Haluatko varmasti merkitä tämän kuljetuksen valmiiksi?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)} disabled={updating}>Peruuta</Button>
          <Button onClick={handleComplete} variant="contained" disabled={updating}>
            {updating ? <CircularProgress size={20} /> : "Vahvista"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Report Problem (also handles revert) ── */}
      <ReportProblemModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSubmit={handleReportSubmit}
        deliveryId={deliveryId}
        submitting={reportSubmitting}
        allowRevert
      />
    </>
  );
}

export default DeliveryDetail;